/**
 * Starfield animation logic for SpaceBackground component
 * Creates animated twinkling stars with pink/orange colors
 */

/**
 *
 */

/**
 *
 */
export type Star = {
  readonly x: number;
  readonly y: number;
  readonly size: number;
  readonly opacity: number; // Mutable for animation
  readonly twinkleSpeed: number;
  readonly twinklePhase: number; // Mutable for animation
  readonly color: string;
};

/**
 *
 */
export class Starfield {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;

  private stars: Array<Star> = [];

  private animationFrameId: number | null = null;
  private readonly starCount: number;

  // White and colored stars palette
  private readonly colors = [
    '#FFFFFF', // White
    '#FFFFFF', // White (more white stars)
    '#FFFFFF', // White (even more white stars)
    '#FFC0CB', // Pink
    '#FFB6C1', // Light Pink
    '#FF69B4', // Hot Pink
    '#FFA07A', // Light Salmon (orange-ish)
    '#FF8C69', // Salmon
    '#FFB347', // Pastel Orange
    '#DA70D6', // Orchid (purple)
    '#8A2BE2', // Blue Violet (purple)
  ];

  /**
   *
   * @param canvas
   * @param starCount
   */
  constructor(canvas: HTMLCanvasElement, starCount: number = 150) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get canvas 2D context');
    }
    this.ctx = ctx;
    this.starCount = starCount;

    this.resize();
    this.initStars();
  }

  /**
   * Initialize stars with random positions and properties
   */
  private initStars(): void {
    // eslint-disable-next-line functional/immutable-data
    this.stars = [];
    for (let i = 0; i < this.starCount; i++) {
      // eslint-disable-next-line functional/immutable-data
      this.stars.push(this.createStar());
    }
  }

  /**
   * Create a single star with random properties
   */
  private createStar(): Star {
    return {
      x: Math.random() * this.canvas.width,
      y: Math.random() * this.canvas.height,
      size: Math.random() * 0.4 + 0.3, // Size between 0.3 and 0.7px (very small points)
      opacity: Math.random() * 0.5 + 0.3, // Opacity between 0.3 and 0.8 (wider range)
      twinkleSpeed: Math.random() * 0.04 + 0.02, // Speed between 0.02 and 0.06 (faster twinkle)
      twinklePhase: Math.random() * Math.PI * 2,
      color: this.colors[Math.floor(Math.random() * this.colors.length)],
    };
  }

  /**
   * Resize canvas to fill window
   */
  public resize(): void {
    this.canvas.width = window.innerWidth;
    this.canvas.height = window.innerHeight;

    // Re-initialize stars if canvas was resized
    if (this.stars.length > 0) {
      this.initStars();
    }
  }

  /**
   * Update star positions and twinkling effect
   */
  private update(): void {
    // eslint-disable-next-line functional/immutable-data
    this.stars = this.stars.map((star) => {
      // Update twinkling phase
      const newTwinklePhase = star.twinklePhase + star.twinkleSpeed;

      // Calculate opacity using sine wave for smooth twinkling
      let newOpacity = (Math.sin(newTwinklePhase) + 1) / 2; // Value between 0 and 1

      // Occasionally add brightness spikes for extra twinkle
      if (Math.random() < 0.002) {
        newOpacity = Math.min(1, newOpacity + 0.4);
      }

      return {
        ...star,
        twinklePhase: newTwinklePhase,
        opacity: newOpacity,
      };
    });
  }

  /**
   * Render stars to canvas
   */
  private render(): void {
    // Clear canvas with dark space background
    this.ctx.fillStyle = '#0a0a0f'; // Very dark blue-black
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    // Draw stars
    for (const star of this.stars) {
      this.ctx.save();

      // Set star color with calculated opacity
      this.ctx.fillStyle = star.color;
      this.ctx.globalAlpha = star.opacity;

      // Minimal glow effect to keep stars small
      this.ctx.shadowBlur = star.size * 0.5;
      this.ctx.shadowColor = star.color;

      // Draw star as a circle
      this.ctx.beginPath();
      this.ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
      this.ctx.fill();

      this.ctx.restore();
    }
  }

  /**
   * Animation loop using RequestAnimationFrame
   */
  private readonly animate = (): void => {
    this.update();
    this.render();
    // eslint-disable-next-line functional/immutable-data
    this.animationFrameId = requestAnimationFrame(this.animate);
  };

  /**
   * Start the animation
   */
  public start(): void {
    if (!this.animationFrameId) {
      this.animate();
    }
  }

  /**
   * Stop the animation
   */
  public stop(): void {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      // eslint-disable-next-line functional/immutable-data
      this.animationFrameId = null;
    }
  }

  /**
   * Clean up resources
   */
  public destroy(): void {
    this.stop();
    // eslint-disable-next-line functional/immutable-data
    this.stars = [];
  }
}
